import { CaretDownIcon, CaretUpIcon } from '@radix-ui/react-icons';
import { Box, Flex, Table } from '@radix-ui/themes';
import { EB } from 'components/layout/error-boundary';
import { CommonTableBodyRow } from 'components/table/body-row';
import { CommonTableToolbar } from 'components/table/toolbar';
import { TableDef } from 'interfaces/table';
import _ from 'lodash';
import { useMemo, useState } from 'react';

export const CommonTable = (props: TableDef) => {
  const [sort, setSort] = useState(props.defaultSort);

  const visibleColumns = useMemo(
    () => props.columns.filter((c) => !c.hidden),
    [props.columns]
  );

  const sortedData = useMemo(() => {
    const data = _.clone(props.data);

    if (!sort) {
      return data;
    }

    const col = visibleColumns[sort.iCol];

    if (!col.sortFn) {
      return data;
    }

    data.sort(visibleColumns[sort.iCol].sortFn);

    if (!sort.asc) {
      data.reverse();
    }

    return data;
  }, [visibleColumns, props.data, sort]);

  const headers = useMemo(
    () =>
      visibleColumns.map((c, i) => (
        <Table.ColumnHeaderCell
          key={`h-${i}`}
          className={c.sortFn ? 'clickable' : undefined}
          onClick={() => {
            if (!c.sortFn) {
              return;
            }

            setSort((prev) => {
              if (!prev) {
                // no sort => asc
                return { iCol: i, asc: true };
              }

              if (prev.iCol !== i) {
                // another column => asc
                return { iCol: i, asc: true };
              }

              // this column
              if (prev.asc) {
                // asc => desc
                return { iCol: i, asc: false };
              }

              // desc => no sort
              return undefined;
            });
          }}
        >
          <Flex gap="1" align="center">
            {c.header}
            {sort &&
              sort.iCol === i &&
              (sort.asc ? <CaretUpIcon /> : <CaretDownIcon />)}
          </Flex>
        </Table.ColumnHeaderCell>
      )),
    [visibleColumns, sort]
  );

  const rows = useMemo(
    () =>
      sortedData.map((item, index) => (
        <CommonTableBodyRow
          key={`r-${index}`}
          index={index}
          item={item}
          rowFns={visibleColumns.map((col) => col.rowFn)}
          rowClassFn={props.rowClassFn}
        />
      )),
    [props.rowClassFn, sortedData, visibleColumns]
  );

  return (
    <EB>
      <Flex direction="column" gap="4">
        <CommonTableToolbar {...props} />

        <Box overflow="auto">
          <Table.Root>
            <Table.Header>
              <Table.Row>{headers}</Table.Row>
            </Table.Header>
            <Table.Body>{rows}</Table.Body>
          </Table.Root>
        </Box>
      </Flex>
    </EB>
  );
};
